import React from 'react';
import { InventoryItem } from '../../api/inventory';
import { finishFromItem, finishLabel, foilTreatmentLabel } from '../../lib/cardVariantOptions';
import { isMagicProductLine } from '../../lib/inventoryNav';
import { gameDisplayName, isTcgplayerListingCandidate } from '../../lib/tcgplayerListing';
import { colors, spacing, typographyFlat, borderRadius } from '../../styles/tokens';
import { CardImage } from '../card/CardImage';

interface InventoryListCardProps {
  item: InventoryItem;
  onEdit: (id: string) => void;
}

const styles = {
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    marginBottom: spacing.sm,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  } as React.CSSProperties,
  cardHover: {
    boxShadow: `0 4px 12px ${colors.text}22`,
    transform: 'translateY(-1px)',
  } as React.CSSProperties,
  imageContainer: {
    flexShrink: 0,
    width: '60px',
    height: '84px',
    borderRadius: borderRadius.xs,
    overflow: 'hidden',
    backgroundColor: colors.background,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative' as const,
  } as React.CSSProperties,
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  } as React.CSSProperties,
  placeholder: {
    fontSize: typographyFlat.fontSize.xs,
    color: colors.textSecondary,
    textAlign: 'center' as const,
  } as React.CSSProperties,
  foilBadge: {
    position: 'absolute' as const,
    bottom: '2px',
    right: '2px',
    backgroundColor: 'rgba(255, 215, 0, 0.9)',
    color: '#000',
    padding: '1px 3px',
    borderRadius: '2px',
    fontSize: '7px',
    fontWeight: typographyFlat.fontWeight.bold,
    textShadow: '0 0.5px 1px rgba(0, 0, 0, 0.2)',
    zIndex: 10,
    lineHeight: '1',
  } as React.CSSProperties,
  stapleBadge: {
    position: 'absolute' as const,
    top: '2px',
    left: '2px',
    backgroundColor: 'rgba(138, 43, 226, 0.9)',
    color: '#fff',
    padding: '1px 3px',
    borderRadius: '2px',
    fontSize: '7px',
    fontWeight: typographyFlat.fontWeight.bold,
    textShadow: '0 0.5px 1px rgba(0, 0, 0, 0.3)',
    zIndex: 10,
    lineHeight: '1',
  } as React.CSSProperties,
  content: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: spacing.md,
  } as React.CSSProperties,
  column: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.xs,
  } as React.CSSProperties,
  label: {
    fontSize: typographyFlat.fontSize.xs,
    color: colors.textSecondary,
    textTransform: 'uppercase' as const,
    fontWeight: typographyFlat.fontWeight.semibold,
  } as React.CSSProperties,
  value: {
    fontSize: typographyFlat.fontSize.sm,
    color: colors.text,
    fontWeight: typographyFlat.fontWeight.medium,
  } as React.CSSProperties,
  actions: {
    flexShrink: 0,
    display: 'flex',
    gap: spacing.xs,
  } as React.CSSProperties,
  actionButton: {
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typographyFlat.fontSize.xs,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.primary,
    backgroundColor: 'transparent',
    border: `1px solid ${colors.primary}`,
    borderRadius: borderRadius.xs,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  } as React.CSSProperties,
};

export const InventoryListCard: React.FC<InventoryListCardProps> = ({
  item,
  onEdit,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isFlipped, setIsFlipped] = React.useState(false);
  const formatPrice = (value?: number | null) => (value != null ? `$${value.toFixed(2)}` : '—');
  const listingStatus = item.tcgplayer_listing?.status
    || (isTcgplayerListingCandidate(item) ? 'Not listed' : '—');
  const magicChrome = isMagicProductLine(item.product_line);
  const treatment = foilTreatmentLabel(item);
  const showImage = Boolean(item.image_url || (magicChrome && item.scryfall_id));

  return (
    <div
      style={{
        ...styles.card,
        ...(isHovered ? styles.cardHover : {}),
      } as React.CSSProperties}
      onClick={() => onEdit(item.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Thumbnail */}
      <div style={styles.imageContainer}>
        {showImage ? (
          <>
            <CardImage
              scryfallId={magicChrome ? item.scryfall_id : undefined}
              reverseScryfallId={magicChrome ? item.reverse_scryfall_id : undefined}
              imageUrl={item.image_url}
              reverseImageUrl={item.reverse_image_url}
              isDualFaced={!!item.is_dual_faced}
              isFlipped={isFlipped}
              size="fill"
              cardName={item.product_name}
              onFlip={(e) => {
                e.stopPropagation();
                setIsFlipped((prev) => !prev);
              }}
            />
            {treatment ? <div style={styles.foilBadge}>{treatment}</div> : null}
            {magicChrome && item.edhrec_rank && item.edhrec_rank <= 500 && <div style={styles.stapleBadge}>STAPLE</div>}
          </>
        ) : (
          <span style={styles.placeholder}>No image</span>
        )}
      </div>

      {/* Content Grid */}
      <div style={styles.content}>
        <div style={styles.column}>
          <div style={styles.label}>Product</div>
          <div style={styles.value}>{item.product_name}</div>
        </div>

        <div style={styles.column}>
          <div style={styles.label}>Game</div>
          <div style={styles.value}>{gameDisplayName(item.product_line)}</div>
        </div>

        <div style={styles.column}>
          <div style={styles.label}>Set</div>
          <div style={styles.value}>{item.set_name}</div>
        </div>

        <div style={styles.column}>
          <div style={styles.label}>Card #</div>
          <div style={styles.value}>{item.collector_number || '—'}</div>
        </div>

        <div style={styles.column}>
          <div style={styles.label}>Condition</div>
          <div style={styles.value}>{item.condition || '—'}</div>
        </div>

        <div style={styles.column}>
          <div style={styles.label}>Finish</div>
          <div style={styles.value}>{finishLabel(Boolean(item.foil), finishFromItem(item))}</div>
        </div>

        <div style={styles.column}>
          <div style={styles.label}>Language</div>
          <div style={styles.value}>{item.language || '—'}</div>
        </div>

        <div style={styles.column}>
          <div style={styles.label}>Qty</div>
          <div style={{ ...styles.value, color: colors.primary, fontWeight: typographyFlat.fontWeight.bold }}>
            {item.quantity}
          </div>
        </div>

        <div style={styles.column}>
          <div style={styles.label}>Avg Cost</div>
          <div style={styles.value}>{formatPrice(item.cost_basis)}</div>
        </div>

        <div style={styles.column}>
          <div style={styles.label}>Preferred Price</div>
          <div style={styles.value}>{formatPrice(item.preferred_price)}</div>
        </div>

        <div style={styles.column}>
          <div style={styles.label}>Policy Price</div>
          <div style={styles.value}>{formatPrice(item.policy_price)}</div>
        </div>

        <div style={styles.column}>
          <div style={styles.label}>ManaPool Listed</div>
          <div style={styles.value}>{formatPrice(item.manapool_listing_price)}</div>
        </div>

        <div style={styles.column}>
          <div style={styles.label}>Manapool Low</div>
          <div style={styles.value}>{formatPrice(item.manapool_low)}</div>
        </div>

        <div style={styles.column}>
          <div style={styles.label}>TCGPlayer</div>
          <div style={styles.value}>{listingStatus}</div>
          {item.tcgplayer_listing?.last_error ? (
            <div style={{ ...styles.label, color: colors.error, textTransform: 'none' }}>
              {item.tcgplayer_listing.last_error}
            </div>
          ) : null}
        </div>

        <div style={styles.column}>
          <div style={styles.label}>TCGPlayer Market</div>
          <div style={styles.value}>{formatPrice(item.tcgplayer_market)}</div>
        </div>

        <div style={styles.column}>
          <div style={styles.label}>ManaPool Market</div>
          <div style={styles.value}>{formatPrice(item.manapool_market)}</div>
        </div>

        <div style={styles.column}>
          <div style={styles.label}>CK Buylist</div>
          <div style={styles.value}>{formatPrice(item.ck_buylist)}</div>
        </div>
      </div>
    </div>
  );
};
